/**
 * Photo storage. Thin on purpose: the interesting decisions — what may be
 * uploaded, how big it is allowed to be — live in images.ts and, for real, in
 * storage.rules, which is the only copy an attacker cannot skip.
 */

import {
  ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject,
} from 'firebase/storage'
import { storage } from '@/firebase'
import { photoPath, rejectionReason, shrink } from '@/lib/images'
import { log } from '@/lib/logger'

const STORAGE_HOST = 'firebasestorage.googleapis.com'

/** True for a photo we hold, as opposed to a shipped reference image or a drawing. */
export function isUploadedPhoto(url: string | null | undefined): boolean {
  return typeof url === 'string' && url.includes(STORAGE_HOST)
}

export interface UploadResult { url: string; path: string }

export async function uploadPhoto(
  file: File,
  folder: 'parts' | 'vehicles' | 'categories',
  ownerId: string,
  onProgress?: (percent: number) => void,
): Promise<UploadResult> {
  const refusal = rejectionReason(file)
  if (refusal) throw new Error(refusal)

  const prepared = await shrink(file)
  const path = photoPath(folder, ownerId, prepared.ext)
  const target = storageRef(storage, path)

  const task = uploadBytesResumable(target, prepared.blob, { contentType: prepared.type })
  await new Promise<void>((resolve, reject) => {
    task.on('state_changed',
      (snap) => onProgress?.(Math.round((snap.bytesTransferred / Math.max(1, snap.totalBytes)) * 100)),
      (err) => reject(new Error(friendly(err))),
      () => resolve(),
    )
  })

  const url = await getDownloadURL(target)
  log.info('photo uploaded', {
    folder, ownerId, path, resized: prepared.resized,
    bytes: prepared.blob.size, originalBytes: file.size,
  })
  return { url, path }
}

/**
 * Best effort. A photo the record no longer points at is invisible either way,
 * so a failed delete must never block the edit that caused it.
 */
export async function deletePhoto(url: string): Promise<void> {
  if (!isUploadedPhoto(url)) return
  try {
    await deleteObject(storageRef(storage, url))
    log.info('photo deleted', { url: '[stored]' })
  } catch (err) {
    log.warn('photo delete failed, leaving the file behind', { err })
  }
}

export async function deletePhotos(urls: readonly (string | undefined | null)[]): Promise<void> {
  await Promise.all(urls.filter((u): u is string => Boolean(u)).map(deletePhoto))
}

function friendly(err: unknown): string {
  const code = String((err as { code?: string }).code ?? '')
  if (code.includes('unauthorized')) return 'Storage refused that. Sign out and back in — your authenticator check may have expired.'
  if (code.includes('canceled')) return 'Upload cancelled.'
  if (code.includes('retry-limit')) return 'The connection dropped before the photo finished. Try again.'
  if (code.includes('quota')) return 'The storage quota is full.'
  return 'That photo did not upload. Try again.'
}
